import {
  EditOutlined,
  FolderAddOutlined,
  FolderOpenOutlined,
  PlusOutlined,
  ReloadOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { TableColumnsType } from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { listCustomers } from '../api/customers';
import type { CustomerListItem } from '../api/customers';
import { ApiError } from '../api/errors';
import {
  SalesOpportunityStage,
  changeOpportunityStage,
  closingStages,
  createOpportunity,
  listOpportunities,
  salesOpportunityStageNames,
  updateOpportunity,
} from '../api/opportunities';
import type {
  SalesOpportunity,
  SalesOpportunityStageValue,
  SalesPipelineSummary,
} from '../api/opportunities';
import { PermissionCodes } from '../auth/permissions';
import { useAuth } from '../auth/useAuth';
import { AppRoutes } from '../routes';
import { BrandColors } from '../theme/brand';
import { formatDate } from './projects/projectDisplay';

/** Giá trị của biểu mẫu khai và sửa cơ hội. */
interface OpportunityFormValues {
  readonly customerId: string;
  readonly name: string;
  readonly description: string | null;
  readonly estimatedValue: number | null;
  readonly currencyCode: string | null;
  readonly winProbabilityPercent: number | null;
  readonly expectedCloseDate: Dayjs | null;
}

/** Giá trị của biểu mẫu chuyển giai đoạn. */
interface StageFormValues {
  readonly stage: SalesOpportunityStageValue;
  readonly outcomeReason: string | null;
  readonly competitor: string | null;
}

/** Màu của từng giai đoạn. */
const stageColors: Readonly<Record<SalesOpportunityStageValue, string>> = {
  [SalesOpportunityStage.Survey]: 'default',
  [SalesOpportunityStage.Quoting]: 'blue',
  [SalesOpportunityStage.Negotiating]: 'processing',

  // Chờ PO tô vàng: đây là chỗ thương vụ nằm lâu nhất mà mình không làm gì được, nên nó phải nổi
  // lên giữa danh sách.
  [SalesOpportunityStage.AwaitingPurchaseOrder]: 'gold',

  [SalesOpportunityStage.Won]: 'success',
  [SalesOpportunityStage.Lost]: 'red',
  [SalesOpportunityStage.Abandoned]: 'default',
};

/** Số khách hàng nạp về cho ô chọn. */
const customerPageSize = 200;

/** Đồng tiền mặc định khi khai cơ hội mới. */
const defaultCurrencyCode = 'VND';

/** Định dạng tiền theo cách đọc của người Việt. */
function formatMoney(value: number | null, currencyCode: string | null): string {
  if (value === null) {
    return '—';
  }

  return `${value.toLocaleString('vi-VN')} ${currencyCode ?? ''}`.trim();
}

/**
 * Trang cơ hội bán hàng.
 *
 * <b>Bảng này trả lời câu hỏi mà bảng dự án không trả lời được:</b> đang theo đuổi bao nhiêu thương
 * vụ, giá trị bao nhiêu, cái nào chờ PO quá lâu, và vì sao những cái đã đóng lại thắng hoặc thua.
 *
 * <b>Giá trị theo trọng số là con số duy nhất cộng lại có nghĩa.</b> Mười cơ hội một tỷ ở mức mười
 * phần trăm không đáng tin bằng một cơ hội một tỷ ở mức chín mươi phần trăm.
 *
 * <b>Mọi phép cộng tiền ở đây bỏ qua đồng tiền</b> vì hệ thống chưa có tỷ giá. Trộn nhiều đồng tiền
 * trong cùng một phễu sẽ cho ra một con số vô nghĩa — đây là giới hạn đã biết, không phải chỗ quên.
 */
export function SalesOpportunityPage(): ReactNode {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();

  const canManage = hasPermission(PermissionCodes.OpportunityManage);

  // Mở dự án là quyền của module dự án, không phải quyền của cơ hội: người khai cơ hội chưa chắc
  // được mở dự án, nên nút phải theo đúng quyền thật.
  const canCreateProject = hasPermission(PermissionCodes.ProjectCreate);

  const [form] = Form.useForm<OpportunityFormValues>();
  const [stageForm] = Form.useForm<StageFormValues>();

  const [stageFilter, setStageFilter] = useState<SalesOpportunityStageValue | null>(null);
  const [onlyOpen, setOnlyOpen] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  const [summary, setSummary] = useState<SalesPipelineSummary | null>(null);
  const [customers, setCustomers] = useState<readonly CustomerListItem[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [editing, setEditing] = useState<SalesOpportunity | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const [stageTarget, setStageTarget] = useState<SalesOpportunity | null>(null);
  const [selectedStage, setSelectedStage] = useState<SalesOpportunityStageValue>(
    SalesOpportunityStage.Quoting,
  );

  const [isSaving, setIsSaving] = useState(false);

  const requestKey = useMemo(
    () => JSON.stringify({ stageFilter, onlyOpen, searchTerm, reloadToken }),
    [stageFilter, onlyOpen, searchTerm, reloadToken],
  );

  useEffect(() => {
    let isCurrent = true;

    async function load(): Promise<void> {
      setErrorMessage(null);

      try {
        const [pipeline, customerPage] = await Promise.all([
          listOpportunities({
            stage: stageFilter ?? undefined,
            onlyOpen: onlyOpen ? true : undefined,
            searchTerm: searchTerm === '' ? undefined : searchTerm,
          }),
          listCustomers({ pageNumber: 1, pageSize: customerPageSize }),
        ]);

        if (!isCurrent) {
          return;
        }

        setSummary(pipeline);
        setCustomers(customerPage.items);
      } catch (error) {
        if (!isCurrent) {
          return;
        }

        setErrorMessage(
          error instanceof ApiError ? error.message : 'Không tải được danh sách cơ hội.',
        );
      }
    }

    void load();

    return () => {
      isCurrent = false;
    };
  }, [requestKey, stageFilter, onlyOpen, searchTerm]);

  const reload = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  function openCreate(): void {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ currencyCode: defaultCurrencyCode, winProbabilityPercent: 30 });
    setIsFormOpen(true);
  }

  function openEdit(opportunity: SalesOpportunity): void {
    setEditing(opportunity);
    form.setFieldsValue({
      customerId: opportunity.customerId,
      name: opportunity.name,
      description: opportunity.description,
      estimatedValue: opportunity.estimatedValue,
      currencyCode: opportunity.currencyCode,
      winProbabilityPercent: opportunity.winProbabilityPercent,
      expectedCloseDate:
        opportunity.expectedCloseDate === null
          ? null
          : dayjs(opportunity.expectedCloseDate.slice(0, 10)),
    });
    setIsFormOpen(true);
  }

  function openStageChange(opportunity: SalesOpportunity): void {
    setStageTarget(opportunity);
    setSelectedStage(SalesOpportunityStage.Quoting);
    stageForm.resetFields();
    stageForm.setFieldsValue({ stage: SalesOpportunityStage.Quoting });
  }

  async function submit(values: OpportunityFormValues): Promise<void> {
    setIsSaving(true);

    try {
      const payload = {
        customerId: values.customerId,
        name: values.name,
        description: values.description ?? null,
        estimatedValue: values.estimatedValue ?? null,

        // Có giá trị thì bắt buộc có đồng tiền, nếu không máy chủ từ chối.
        currencyCode:
          values.estimatedValue === null ? null : (values.currencyCode ?? defaultCurrencyCode),
        winProbabilityPercent: values.winProbabilityPercent ?? null,
        expectedCloseDate:
          values.expectedCloseDate === null ? null : values.expectedCloseDate.format('YYYY-MM-DD'),
        ownerUserId: editing?.ownerUserId ?? null,
      };

      if (editing === null) {
        const created = await createOpportunity(payload);

        message.success(`Đã khai cơ hội ${created.code}.`);
      } else {
        await updateOpportunity(editing.id, payload);
        message.success('Đã lưu thay đổi.');
      }

      setIsFormOpen(false);
      reload();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không lưu được.');
    } finally {
      setIsSaving(false);
    }
  }

  async function submitStage(values: StageFormValues): Promise<void> {
    if (stageTarget === null) {
      return;
    }

    setIsSaving(true);

    try {
      await changeOpportunityStage(stageTarget.id, {
        stage: values.stage,
        outcomeReason: values.outcomeReason ?? null,
        competitor: values.competitor ?? null,
      });

      message.success(`Đã chuyển sang giai đoạn ${salesOpportunityStageNames[values.stage]}.`);
      setStageTarget(null);
      reload();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không chuyển được giai đoạn.');
    } finally {
      setIsSaving(false);
    }
  }

  /**
   * Sang màn hình mở dự án, mang theo khách hàng và tên của cơ hội.
   *
   * Không dựng biểu mẫu mở dự án ở đây: mở dự án có bảy ô và vài quy tắc riêng, dựng bản thứ hai
   * nghĩa là hai biểu mẫu giống nhau ở hai chỗ và chúng sẽ lệch nhau ở lần sửa sau.
   */
  function openProjectFrom(opportunity: SalesOpportunity): void {
    const parameters = new URLSearchParams({
      'co-hoi': opportunity.id,
      'khach-hang': opportunity.customerId,
      ten: opportunity.name,
    });

    void navigate(`${AppRoutes.Project}?${parameters.toString()}`);
  }

  const isClosingStage = closingStages.includes(selectedStage);

  const columns: TableColumnsType<SalesOpportunity> = [
    {
      title: 'Mã',
      dataIndex: 'code',
      width: 130,
      render: (code: string) => <Typography.Text strong>{code}</Typography.Text>,
    },
    {
      title: 'Cơ hội',
      dataIndex: 'name',
      render: (name: string, opportunity) => (
        <Space direction="vertical" size={0}>
          <span>{name}</span>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {opportunity.customerName ?? '—'}
          </Typography.Text>
          {opportunity.outcomeReason === null ? null : (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {opportunity.competitor === null ? '' : `Thua ${opportunity.competitor}: `}
              {opportunity.outcomeReason}
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Giai đoạn',
      dataIndex: 'stage',
      width: 130,
      render: (stage: SalesOpportunityStageValue) => (
        <Tag color={stageColors[stage]}>{salesOpportunityStageNames[stage]}</Tag>
      ),
    },
    {
      title: 'Giá trị',
      dataIndex: 'estimatedValue',
      width: 170,
      align: 'right',
      render: (value: number | null, opportunity) =>
        formatMoney(value, opportunity.currencyCode),
    },
    {
      title: 'Xác suất',
      dataIndex: 'winProbabilityPercent',
      width: 100,
      align: 'right',
      render: (percent: number | null) => (percent === null ? '—' : `${String(percent)}%`),
    },
    {
      title: 'Theo trọng số',
      dataIndex: 'weightedValue',
      width: 170,
      align: 'right',
      render: (value: number | null, opportunity) =>
        formatMoney(value, opportunity.currencyCode),
    },
    {
      title: 'Dự kiến chốt',
      dataIndex: 'expectedCloseDate',
      width: 140,
      render: (value: string | null, opportunity) =>
        opportunity.isOverdue ? (
          <Tooltip title="Đã qua ngày dự kiến chốt mà cơ hội vẫn chưa đóng.">
            <Tag color="red">{formatDate(value)}</Tag>
          </Tooltip>
        ) : (
          formatDate(value)
        ),
    },
    {
      title: 'Số ngày',
      dataIndex: 'daysOpen',
      width: 100,
      align: 'right',
      render: (days: number) => days,
    },
    {
      title: '',
      key: 'actions',
      width: 140,
      render: (_: unknown, opportunity) => (
        <Space size={2}>
          {/*
            Ba trạng thái, ba thứ khác nhau hiện ra ở đây:

            - Cơ hội đã thắng mà CHƯA có dự án: nút mở dự án. Đây là bước tiếp theo tự nhiên, và
              để người dùng tự nhớ sang màn hình khác rồi gõ lại tên khách hàng là chỗ dễ bỏ sót
              nhất của cả luồng.
            - Đã có dự án: nút mở thẳng dự án đó, để không ai mở nhầm dự án thứ hai cho cùng một
              thương vụ.
            - Chưa thắng: không hiện gì — mở dự án cho một thương vụ chưa chốt là chuyện khác, và
              máy chủ cũng từ chối gắn.
          */}
          {opportunity.stage === SalesOpportunityStage.Won && opportunity.projectId === null ? (
            <Tooltip title="Mở dự án từ cơ hội này">
              <Button
                size="small"
                type="primary"
                icon={<FolderAddOutlined />}
                disabled={!canCreateProject}
                onClick={() => {
                  openProjectFrom(opportunity);
                }}
              />
            </Tooltip>
          ) : null}
          {opportunity.projectId === null ? null : (
            <Tooltip title="Mở dự án đã gắn với cơ hội này">
              <Button
                size="small"
                icon={<FolderOpenOutlined />}
                onClick={() => {
                  void navigate(`${AppRoutes.Project}/${opportunity.projectId ?? ''}`);
                }}
              />
            </Tooltip>
          )}
          <Tooltip
            title={
              opportunity.isClosed
                ? 'Cơ hội đã đóng thì không sửa được nữa.'
                : 'Sửa thông tin'
            }
          >
            <Button
              size="small"
              icon={<EditOutlined />}
              disabled={!canManage || opportunity.isClosed}
              onClick={() => {
                openEdit(opportunity);
              }}
            />
          </Tooltip>
          <Tooltip
            title={
              opportunity.isClosed
                ? 'Cơ hội đã đóng không quay lui được. Thương vụ quay lại thì khai cơ hội mới.'
                : 'Chuyển giai đoạn'
            }
          >
            <Button
              size="small"
              type="primary"
              icon={<SwapOutlined />}
              disabled={!canManage || opportunity.isClosed}
              onClick={() => {
                openStageChange(opportunity);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space style={{ justifyContent: 'space-between', width: '100%' }} wrap>
        <Space direction="vertical" size={0}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Cơ hội bán hàng
          </Typography.Title>
          <Typography.Text type="secondary">
            Thương vụ đang theo đuổi, chưa thành dự án. Thắng thì mở dự án; thua hay khách dừng thì
            lý do được giữ lại để lần báo giá sau sát hơn.
          </Typography.Text>
        </Space>
        <Space wrap>
          <Input.Search
            allowClear
            placeholder="Tìm theo mã hoặc tên"
            style={{ width: 220 }}
            onSearch={setSearchTerm}
          />
          <Select<SalesOpportunityStageValue | null>
            allowClear
            placeholder="Lọc theo giai đoạn"
            style={{ minWidth: 170 }}
            value={stageFilter}
            onChange={(value) => {
              setStageFilter(value ?? null);
            }}
            options={Object.values(SalesOpportunityStage).map((stage) => ({
              value: stage,
              label: salesOpportunityStageNames[stage],
            }))}
          />
          <Tooltip title="Chỉ hiện thương vụ chưa đóng">
            <Switch
              checkedChildren="Đang theo"
              unCheckedChildren="Tất cả"
              checked={onlyOpen}
              onChange={setOnlyOpen}
            />
          </Tooltip>
          <Button icon={<ReloadOutlined />} onClick={reload}>
            Tải lại
          </Button>
          <Button type="primary" icon={<PlusOutlined />} disabled={!canManage} onClick={openCreate}>
            Khai cơ hội
          </Button>
        </Space>
      </Space>

      {errorMessage === null ? null : <Alert type="error" showIcon message={errorMessage} />}

      {summary === null ? null : (
        <Space size="large" wrap>
          <Statistic title="Đang theo đuổi" value={summary.openCount} />
          <Statistic
            title="Quá hạn chốt"
            value={summary.overdueCount}
            valueStyle={summary.overdueCount > 0 ? { color: BrandColors.red } : {}}
          />
          <Statistic
            title="Tổng giá trị đang mở"
            value={summary.openValue}
            formatter={(value) => Number(value).toLocaleString('vi-VN')}
          />
          <Statistic
            title="Theo trọng số"
            value={summary.openWeightedValue}
            formatter={(value) => Number(value).toLocaleString('vi-VN')}
          />
          <Statistic
            title="Tỷ lệ thắng"
            value={summary.winRatePercent ?? 0}
            suffix="%"
            precision={1}
          />
          <Statistic
            title="Thắng / Thua / Khách dừng"
            value={`${String(summary.wonCount)} / ${String(summary.lostCount)} / ${String(summary.abandonedCount)}`}
          />
        </Space>
      )}

      <Alert
        type="info"
        showIcon
        message="Tỷ lệ thắng không tính cơ hội khách dừng"
        description="Khách hoãn đầu tư không phải là mình thua ai. Gộp vào sẽ làm con số này tụt xuống vì những chuyện nằm ngoài tầm kiểm soát. Lưu ý thêm: các tổng tiền ở đây cộng thẳng, chưa quy đổi tỷ giá — hãy lọc theo một đồng tiền nếu công ty có thương vụ ngoại tệ."
      />

      <Table<SalesOpportunity>
        rowKey="id"
        size="small"
        loading={summary === null && errorMessage === null}
        pagination={false}
        columns={columns}
        dataSource={[...(summary?.opportunities ?? [])]}
        locale={{
          emptyText: 'Chưa có cơ hội nào. Khai thương vụ đang theo đuổi để thấy được cả phễu.',
        }}
      />

      <Modal
        open={isFormOpen}
        title={editing === null ? 'Khai cơ hội mới' : `Sửa cơ hội ${editing.code}`}
        okText="Lưu"
        cancelText="Đóng"
        confirmLoading={isSaving}
        onCancel={() => {
          setIsFormOpen(false);
        }}
        onOk={() => {
          form.submit();
        }}
      >
        <Form form={form} layout="vertical" onFinish={(values) => void submit(values)}>
          <Form.Item
            name="customerId"
            label="Khách hàng"
            tooltip={
              editing === null
                ? 'Chưa có khách hàng trong hệ thống thì khai ở màn hình Khách hàng trước.'
                : 'Không đổi được khách hàng của cơ hội đã khai.'
            }
            rules={[{ required: true, message: 'Chọn khách hàng.' }]}
          >
            <Select
              showSearch
              disabled={editing !== null}
              optionFilterProp="label"
              placeholder="Chọn khách hàng"
              options={customers.map((customer) => ({
                value: customer.id,
                label: `${customer.code} — ${customer.shortName}`,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="name"
            label="Tên cơ hội"
            tooltip="Mã cơ hội do hệ thống tự sinh, không phải gõ."
            rules={[{ required: true, message: 'Nhập tên cơ hội.' }, { max: 200 }]}
          >
            <Input placeholder="AOI kiểm tra ngoại quan — nhà máy Bắc Giang" />
          </Form.Item>
          <Form.Item name="description" label="Nhu cầu và phạm vi dự kiến" rules={[{ max: 4000 }]}>
            <Input.TextArea
              rows={3}
              placeholder="Khách cần gì, dây chuyền nào, có ràng buộc gì về thời gian hay mặt bằng."
            />
          </Form.Item>
          <Space align="start" wrap size="middle" style={{ width: '100%' }}>
            <Form.Item name="estimatedValue" label="Giá trị dự kiến" style={{ minWidth: 220 }}>
              <InputNumber
                min={0}
                step={10_000_000}
                style={{ width: '100%' }}
                formatter={(value) => (value === undefined ? '' : value.toLocaleString('vi-VN'))}
              />
            </Form.Item>
            <Form.Item name="currencyCode" label="Đồng tiền" style={{ minWidth: 120 }}>
              <Select
                options={[
                  { value: 'VND', label: 'VND' },
                  { value: 'USD', label: 'USD' },
                  { value: 'EUR', label: 'EUR' },
                  { value: 'JPY', label: 'JPY' },
                ]}
              />
            </Form.Item>
            <Form.Item
              name="winProbabilityPercent"
              label="Xác suất thắng (%)"
              tooltip="Tự đánh giá, không tính tự động theo giai đoạn — con số tự động chỉ là đổi tên của giai đoạn."
              style={{ minWidth: 160 }}
            >
              <InputNumber min={0} max={100} step={10} style={{ width: '100%' }} />
            </Form.Item>
          </Space>
          <Form.Item name="expectedCloseDate" label="Ngày dự kiến chốt">
            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={stageTarget !== null}
        title={stageTarget === null ? '' : `Chuyển giai đoạn — ${stageTarget.code}`}
        okText="Lưu"
        cancelText="Đóng"
        confirmLoading={isSaving}
        onCancel={() => {
          setStageTarget(null);
        }}
        onOk={() => {
          stageForm.submit();
        }}
      >
        <Form form={stageForm} layout="vertical" onFinish={(values) => void submitStage(values)}>
          <Form.Item
            name="stage"
            label="Giai đoạn mới"
            rules={[{ required: true, message: 'Chọn giai đoạn.' }]}
          >
            <Select
              onChange={setSelectedStage}
              options={Object.values(SalesOpportunityStage).map((stage) => ({
                value: stage,
                label: salesOpportunityStageNames[stage],
              }))}
            />
          </Form.Item>

          {isClosingStage ? (
            <>
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message="Đóng cơ hội thì không quay lui được"
                description="Lý do sẽ được giữ lại vĩnh viễn — đây là dữ liệu duy nhất giúp lần báo giá sau sát hơn lần này. Thương vụ quay lại thật thì khai một cơ hội mới."
              />
              <Form.Item
                name="outcomeReason"
                label="Lý do"
                rules={[
                  { required: true, message: 'Ghi lý do thắng, thua hoặc vì sao khách dừng.' },
                  { min: 10, message: 'Viết đủ một câu để một năm sau đọc lại còn hiểu.' },
                  { max: 2000 },
                ]}
              >
                <Input.TextArea
                  rows={3}
                  placeholder="Giá cao hơn 18% so với đơn vị trúng, khách chọn phương án rẻ hơn."
                />
              </Form.Item>
              {selectedStage === SalesOpportunityStage.Lost ? (
                <Form.Item
                  name="competitor"
                  label="Đơn vị đã lấy mất thương vụ"
                  tooltip="Chỉ khai khi thua. Đây là dữ liệu để biết mình hay thua ai nhất."
                  rules={[{ max: 200 }]}
                >
                  <Input placeholder="Công ty TNHH Tự động hóa ABC" />
                </Form.Item>
              ) : null}
            </>
          ) : null}
        </Form>
      </Modal>
    </Space>
  );
}
